import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { LinkType, SiteId, TaskId } from '@interfaces/IScheduler';
import { Modal } from '@components/ui/Modal/Modal';
import { Button } from '@components/ui/Button/Button';
import { useSiteLocations } from '@hooks/useSiteLocations';
import { useSchedulerActions, useSchedulerSite } from '../../hooks/useSchedulerStore';
import { useSchedulerPortalContainer } from '../../context/SchedulerPortalContainer';
import {
  buildLocationSplit,
  DEFAULT_SPLIT_LINK,
  type ISplitLink,
  type ISplitRow,
  type SplitMode,
} from '../../lib/splitTaskByLocations';
import { ScrollArea } from '@components/ui/ScrollArea/ScrollArea';
import styles from './SplitByLocationModal.module.css';

// ---------------------------------------------------------------------------
// SplitByLocationModal — break a task into one leaf subtask per location.
//
// Reached from the Locations tab AFTER areas are assigned. The user picks the
// granularity (per floor / per area), tunes each subtask's duration, and picks
// a scheduling mode: dependencies (consecutive subtasks chained with ONE
// shared link type + gap) or parallel (all share the parent's start, no
// links). Generation is delegated to `buildLocationSplit`; the whole split
// lands as a single undoable action.
// ---------------------------------------------------------------------------

type Granularity = 'floor' | 'area';

const DEFAULT_DURATION = 3;
const LINK_TYPES: LinkType[] = ['FS', 'SS', 'FF', 'SF'];

// Mini three-bar diagram for the mode illustration (viewBox 188×56). In
// dependencies mode the layout AND arrows are computed from the selected link
// type + gap, so the picture mirrors what the split will produce; parallel
// left-aligns the bars with no arrows. Same element order in both modes so
// the rects can CSS-transition between layouts.
const DIAG_BAR_W = 44;
const DIAG_BAR_H = 12;
const DIAG_ROW_Y = [3, 22, 41];
const DIAG_PX_PER_DAY = 8;
const DIAG_MAX_LAG_PX = 3 * DIAG_PX_PER_DAY;

interface IDiagramBar {
  x: number;
  y: number;
  w: number;
}

function diagramBars(mode: SplitMode, link: ISplitLink): IDiagramBar[] {
  if (mode === 'parallel') {
    return [
      { x: 6, y: DIAG_ROW_Y[0]!, w: 88 },
      { x: 6, y: DIAG_ROW_Y[1]!, w: 64 },
      { x: 6, y: DIAG_ROW_Y[2]!, w: 76 },
    ];
  }
  // The gap's visual effect is clamped to ±3 days so the diagram never
  // overflows the viewBox; direction and link anchor stay truthful.
  const lagPx = Math.max(-DIAG_MAX_LAG_PX, Math.min(DIAG_MAX_LAG_PX, link.lagDays * DIAG_PX_PER_DAY));
  const xs = [0];
  for (let i = 1; i < 3; i++) {
    const prev = xs[i - 1]!;
    switch (link.type) {
      case 'FS': xs.push(prev + DIAG_BAR_W + lagPx); break; // start at pred end + gap
      case 'SS': xs.push(prev + lagPx); break;              // start at pred start + gap
      case 'FF': xs.push(prev + lagPx); break;              // end at pred end + gap (equal widths)
      case 'SF': xs.push(prev - DIAG_BAR_W + lagPx); break; // end at pred start + gap
    }
  }
  // Left-pad to 6 so the elbow routes of start-anchored links stay in view.
  const minX = Math.min(...xs);
  return xs.map((x, i) => ({ x: x - minX + 6, y: DIAG_ROW_Y[i]!, w: DIAG_BAR_W }));
}

/** Elbow + arrowhead path data for one predecessor→successor pair. */
function diagramArrow(pred: IDiagramBar, succ: IDiagramBar, type: LinkType): { line: string; head: string } {
  const predMidY = pred.y + DIAG_BAR_H / 2;
  const succMidY = succ.y + DIAG_BAR_H / 2;
  const predRight = pred.x + pred.w;
  const succRight = succ.x + succ.w;
  switch (type) {
    case 'FS': {
      // Out of the predecessor's END, down onto the successor's START.
      const dropX = Math.min(Math.max(predRight + 5, succ.x + 6), succRight - 4);
      return {
        line: `M${predRight} ${predMidY} H${dropX} V${succ.y - 5.5}`,
        head: `M${dropX - 3.2} ${succ.y - 5.5} H${dropX + 3.2} L${dropX} ${succ.y} Z`,
      };
    }
    case 'SS': {
      // Out of the predecessor's START, around the left, into the successor's START.
      const elbowX = Math.min(pred.x, succ.x) - 5;
      return {
        line: `M${pred.x} ${predMidY} H${elbowX} V${succMidY} H${succ.x - 5.5}`,
        head: `M${succ.x - 5.5} ${succMidY - 3.2} V${succMidY + 3.2} L${succ.x} ${succMidY} Z`,
      };
    }
    case 'FF': {
      // Out of the predecessor's END, around the right, into the successor's END.
      const elbowX = Math.max(predRight, succRight) + 5;
      return {
        line: `M${predRight} ${predMidY} H${elbowX} V${succMidY} H${succRight + 5.5}`,
        head: `M${succRight + 5.5} ${succMidY - 3.2} V${succMidY + 3.2} L${succRight} ${succMidY} Z`,
      };
    }
    case 'SF': {
      // Out of the predecessor's START, into the successor's END from the right.
      const elbowX = succRight + 5;
      return {
        line: `M${pred.x} ${predMidY} H${elbowX} V${succMidY}`,
        head: `M${succRight + 5.5} ${succMidY - 3.2} V${succMidY + 3.2} L${succRight} ${succMidY} Z`,
      };
    }
  }
}

/** Decorative mode preview: 3 bars laid out by the chosen link + gap, or in parallel. */
function ModeDiagram({ mode, link }: { mode: SplitMode; link: ISplitLink }) {
  const bars = diagramBars(mode, link);
  return (
    <svg className={styles.modeDiagram} viewBox="0 0 188 56" width="188" height="56" aria-hidden>
      {bars.map((bar, i) => (
        <rect key={i} className={styles.diagramBar} x={bar.x} y={bar.y} width={bar.w} height={DIAG_BAR_H} rx={3} />
      ))}
      {mode === 'dependencies' &&
        [0, 1].map((i) => {
          const { line, head } = diagramArrow(bars[i]!, bars[i + 1]!, link.type);
          return (
            <g key={i}>
              <path className={styles.diagramArrow} d={line} />
              <path className={styles.diagramArrowHead} d={head} />
            </g>
          );
        })}
    </svg>
  );
}

/** A row before its (modal-controlled) duration is applied. */
type BaseRow = Omit<ISplitRow, 'durationDays'>;

interface ISplitByLocationModalProps {
  open: boolean;
  onClose: () => void;
  siteId: SiteId;
  taskId: TaskId;
}

export function SplitByLocationModal({ open, onClose, siteId, taskId }: ISplitByLocationModalProps) {
  const { t } = useTranslation();
  const site = useSchedulerSite(siteId);
  const catalog = useSiteLocations(siteId);
  const { splitTaskByLocations } = useSchedulerActions();
  const portalContainer = useSchedulerPortalContainer();

  const [granularity, setGranularity] = useState<Granularity>('area');
  const [mode, setMode] = useState<SplitMode>('dependencies');
  // One link (type + gap) shared by every consecutive pair in dependencies mode.
  const [link, setLink] = useState<ISplitLink>(DEFAULT_SPLIT_LINK);
  const [defaultDuration, setDefaultDuration] = useState(DEFAULT_DURATION);
  // Per-row duration overrides, keyed by row.key (floor id or zone id). Floor
  // and zone keys never collide, so one map survives a granularity switch.
  const [durations, setDurations] = useState<Record<string, number>>({});

  const task = site?.tasks.find((tk) => tk.id === taskId);

  // The task's assigned zones, grouped by floor in catalog order.
  const assignedByFloor = useMemo(() => {
    const assigned = new Set((task?.locations ?? []).map((loc) => loc.locationId));
    return catalog.floors
      .map((floor) => ({ floor, zones: floor.zones.filter((zone) => assigned.has(zone.id)) }))
      .filter((entry) => entry.zones.length > 0);
  }, [catalog.floors, task?.locations]);

  const floorRows = useMemo<BaseRow[]>(
    () =>
      assignedByFloor.map(({ floor, zones }) => ({
        key: floor.id,
        label: floor.name,
        locationIds: zones.map((zone) => zone.id),
      })),
    [assignedByFloor],
  );

  const areaRows = useMemo<BaseRow[]>(
    () =>
      assignedByFloor.flatMap(({ floor, zones }) =>
        zones.map((zone) => ({
          key: zone.id,
          label: t('scheduler.panel.locations.cellLabel', { floor: floor.name, zone: zone.name }),
          locationIds: [zone.id],
        })),
      ),
    [assignedByFloor, t],
  );

  const floorCount = floorRows.length;
  const areaCount = areaRows.length;

  // Resolve the effective rows (with durations) for the active granularity.
  const baseRows = granularity === 'floor' ? floorRows : areaRows;
  const rows = useMemo<ISplitRow[]>(
    () => baseRows.map((row) => ({ ...row, durationDays: durations[row.key] ?? defaultDuration })),
    [baseRows, durations, defaultDuration],
  );

  if (!task) return null;

  const granularityDisabled = (g: Granularity) => (g === 'floor' ? floorCount < 2 : areaCount < 2);
  const canConfirm = rows.length >= 2 && rows.every((row) => row.durationDays >= 1);

  const setRowDuration = (key: string, value: number) =>
    setDurations((prev) => ({ ...prev, [key]: value }));

  const handleConfirm = () => {
    const { subtasks, dependencies } = buildLocationSplit({ task, rows, mode, link });
    splitTaskByLocations({ siteId, parentTaskId: taskId, subtasks, dependencies });
    onClose();
  };

  const footer = (
    <>
      <div className={styles.footerSpacer} />
      <Button variant="ghost" size="sm" onClick={onClose}>
        {t('scheduler.splitModal.cancel')}
      </Button>
      <Button variant="primary" size="sm" onClick={handleConfirm} disabled={!canConfirm}>
        {t('scheduler.splitModal.confirm', { count: rows.length })}
      </Button>
    </>
  );

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={t('scheduler.splitModal.title')}
      description={t('scheduler.splitModal.description')}
      footer={footer}
      size="md"
      container={portalContainer}
    >
      <div className={styles.body}>
        {/* Granularity — per floor vs per area. */}
        <div className={styles.field}>
          <span className={styles.fieldLabel}>{t('scheduler.splitModal.granularity')}</span>
          <div className={styles.segmented} role="group">
            {(['floor', 'area'] as const).map((g) => {
              const disabled = granularityDisabled(g);
              return (
                <button
                  key={g}
                  type="button"
                  className={styles.segment}
                  aria-pressed={granularity === g}
                  disabled={disabled}
                  title={
                    disabled
                      ? t(g === 'floor' ? 'scheduler.splitModal.byFloorDisabled' : 'scheduler.splitModal.byAreaDisabled')
                      : undefined
                  }
                  onClick={() => setGranularity(g)}
                >
                  {t(g === 'floor' ? 'scheduler.splitModal.byFloor' : 'scheduler.splitModal.byArea')}
                  <span className={styles.segmentCount}>{g === 'floor' ? floorCount : areaCount}</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Default duration applied to rows without an override. */}
        <div className={styles.field}>
          <label className={styles.fieldLabel} htmlFor="split-default-duration">
            {t('scheduler.splitModal.defaultDuration')}
          </label>
          <div className={styles.durationInput}>
            <input
              id="split-default-duration"
              type="number"
              min={1}
              value={defaultDuration}
              onChange={(e) => setDefaultDuration(Math.max(1, Math.floor(Number(e.target.value) || 1)))}
            />
            <span className={styles.unit}>{t('scheduler.splitModal.days')}</span>
          </div>
        </div>

        {/* Per-subtask rows. */}
        <div className={styles.subtasks}>
          <span className={styles.fieldLabel}>{t('scheduler.splitModal.subtasks', { count: rows.length })}</span>
          <ScrollArea className={styles.rowScroll}>
            <ul className={styles.rowList}>
              {rows.map((row, i) => (
                <li key={row.key} className={styles.row}>
                  <span className={styles.rowOrder} aria-hidden>{i + 1}</span>
                  <span className={styles.rowLabel}>{row.label}</span>
                  <div className={styles.durationInput}>
                    <input
                      type="number"
                      min={1}
                      aria-label={t('scheduler.splitModal.durationLabel', { name: row.label })}
                      value={row.durationDays}
                      onChange={(e) => setRowDuration(row.key, Math.max(1, Math.floor(Number(e.target.value) || 1)))}
                    />
                    <span className={styles.unit}>{t('scheduler.splitModal.days')}</span>
                  </div>
                </li>
              ))}
            </ul>
          </ScrollArea>
        </div>

        {/* Scheduling mode — chained with one shared link + gap, vs parallel. */}
        <div className={styles.field}>
          <span className={styles.fieldLabel}>{t('scheduler.splitModal.mode')}</span>
          <div className={styles.segmented} role="group">
            {(['dependencies', 'parallel'] as const).map((m) => (
              <button
                key={m}
                type="button"
                className={styles.segment}
                aria-pressed={mode === m}
                onClick={() => setMode(m)}
              >
                {t(m === 'dependencies' ? 'scheduler.splitModal.modeDependencies' : 'scheduler.splitModal.modeParallel')}
              </button>
            ))}
          </div>
          <p className={styles.modeHint}>
            {t(mode === 'dependencies' ? 'scheduler.splitModal.dependenciesHint' : 'scheduler.splitModal.parallelHint')}
          </p>
          {/* Link config (type + gap, applied between every consecutive pair)
              side by side with the live diagram. */}
          <div className={styles.linkRow}>
            {mode === 'dependencies' && (
              <div className={styles.linkControls}>
                <select
                  className={styles.linkSelect}
                  value={link.type}
                  aria-label={t('scheduler.splitModal.linkTypeLabel')}
                  onChange={(e) => setLink((prev) => ({ ...prev, type: e.target.value as LinkType }))}
                >
                  {LINK_TYPES.map((type) => (
                    <option key={type} value={type}>{t(`scheduler.linkType.${type}`)}</option>
                  ))}
                </select>
                <div className={styles.durationInput}>
                  <input
                    type="number"
                    aria-label={t('scheduler.splitModal.gapLabel')}
                    value={link.lagDays}
                    onChange={(e) => setLink((prev) => ({ ...prev, lagDays: Math.floor(Number(e.target.value) || 0) }))}
                  />
                  <span className={styles.unit}>{t('scheduler.splitModal.gapUnit')}</span>
                </div>
              </div>
            )}
            <ModeDiagram mode={mode} link={link} />
          </div>
        </div>
      </div>
    </Modal>
  );
}
